import React, { useRef, useState, useMemo } from 'react';
import { Championship, ChampionshipRecurrence, ChampionshipEvent, ChampionshipRound, Car, CarConfig, Track } from '../types';
import { Info, Settings, Users, Calendar, Radio, X } from 'lucide-react';
import CarSelectionTable from './CarSelectionTable';
import api from '../api/client';
import './WebsiteConfig.css';

interface ChampionshipDetailsTabProps {
  championship: Championship;
  cars: Car[];
  tracks: Track[];
  onChange: (updates: Partial<Championship>) => void;
}

const recurrenceOptions: { value: ChampionshipRecurrence; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom' },
];

const makeDefaultRound = (track = '', layout = '', eventNumber = 1, roundNumber = 1): ChampionshipRound => ({
  id: `round-${eventNumber}-${roundNumber}-${Date.now()}`,
  roundNumber,
  track,
  layout,
  date: '',
  startTime: '20:00',
  timezone: 'Europe/Amsterdam',
  sessions: [],
  raceId: '',
});

const ChampionshipDetailsTab: React.FC<ChampionshipDetailsTabProps> = ({
  championship,
  cars,
  tracks,
  onChange,
}) => {
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const rulebookType = championship.rulebookType || 'text';

  const [simgridImportId, setSimgridImportId] = useState('');
  const [simgridImporting, setSimgridImporting] = useState(false);
  const [simgridImportError, setSimgridImportError] = useState<string | null>(null);

  const [showPointsModal, setShowPointsModal] = useState(false);
  const pointsSystem = championship.pointsSystem || [25, 18, 15, 12, 10, 8, 6, 4, 2, 1];

  const setPointsAtPosition = (index: number, value: number) => {
    const next = [...pointsSystem];
    next[index] = value;
    onChange({ pointsSystem: next });
  };

  const applyPointsSchema = (schema: number[]) => {
    onChange({ pointsSystem: schema });
  };

  // Minimum pit slots across all selected track/layout combos in events
  const minTrackSlots = useMemo(() => {
    const allRounds = (championship.events || []).flatMap((e) => e.rounds || []);
    const slots = allRounds
      .map((r) => tracks.find((t) => t.track === r.track && t.layout === r.layout)?.max_pit_slot)
      .filter((s): s is number => s !== undefined);
    return slots.length > 0 ? Math.min(...slots) : null;
  }, [championship.events, tracks]);

  const shortestRaceDuration = useMemo(() => {
    const durations = (championship.events || [])
      .flatMap((event) => event.rounds || [])
      .map((round) => Number(round.raceLength) || 0)
      .filter((duration) => duration > 0);
    return durations.length > 0 ? Math.min(...durations) : 0;
  }, [championship.events]);

  const handleSimgridImport = async () => {
    const idNum = parseInt(simgridImportId.trim(), 10);
    if (!idNum) {
      setSimgridImportError('Enter a valid SimGrid championship ID');
      return;
    }
    setSimgridImporting(true);
    setSimgridImportError(null);
    try {
      const res = await api.fetchSimgridChampionship(idNum);
      const data = res.data;

      const importedEvents: ChampionshipEvent[] = (data.races || []).map((race: any, idx: number) => {
        const compositeName: string = race.track?.composite_name || '';
        const dashIdx = compositeName.indexOf(' - ');
        const trackName = dashIdx !== -1 ? compositeName.slice(0, dashIdx) : compositeName;
        const layout = dashIdx !== -1 ? compositeName.slice(dashIdx + 3) : '';

        const startsAt = race.starts_at ? new Date(race.starts_at) : null;
        const dateStr = startsAt ? startsAt.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
        const timeStr = startsAt
          ? `${String(startsAt.getUTCHours()).padStart(2, '0')}:${String(startsAt.getUTCMinutes()).padStart(2, '0')}`
          : '20:00';

        const round: ChampionshipRound = {
          ...makeDefaultRound(trackName, layout, idx + 1, 1),
          date: dateStr,
          startTime: timeStr,
          timezone: 'UTC',
          simgridRaceId: race.id,
        };

        return {
          id: `event-${idx + 1}-${Date.now()}`,
          eventNumber: idx + 1,
          rounds: [round],
          allowed_cars_list_full: [],
        };
      });

      onChange({
        name: data.name || championship.name,
        ...(importedEvents.length > 0 ? { events: importedEvents, recurrence: 'custom' } : {}),
        simgridChampionshipId: idNum,
      });
    } catch (err: any) {
      console.error('SimGrid import failed:', err);
      setSimgridImportError(err.response?.data?.error || 'Failed to fetch from SimGrid. Check the API key and championship ID.');
    } finally {
      setSimgridImporting(false);
    }
  };

  const handleChange = (field: keyof Championship, value: any) => {
    onChange({ [field]: value });
  };

  const sharedAllowedCars = championship.events?.[0]?.allowed_cars_list_full || [];

  const updateSharedAllowedCars = (nextCars: CarConfig[]) => {
    onChange({
      events: (championship.events || []).map((event) => ({
        ...event,
        allowed_cars_list_full: nextCars,
      })),
    });
  };

  const handleSharedCarSelectionChange = (carName: string, selected: boolean) => {
    const nextCars = selected
      ? sharedAllowedCars.some((car) => car.car_name === carName)
        ? sharedAllowedCars
        : [...sharedAllowedCars, { car_name: carName, ballast: 0, restrictor: 0 }]
      : sharedAllowedCars.filter((car) => car.car_name !== carName);

    updateSharedAllowedCars(nextCars);
  };

  const handleSharedCarConfigChange = (carName: string, ballast: number, restrictor: number) => {
    const nextCars = sharedAllowedCars.map((car) =>
      car.car_name === carName ? { ...car, ballast, restrictor } : car
    );
    updateSharedAllowedCars(nextCars);
  };

  const isDraft = (championship.status ?? 'draft') === 'draft';

  return (
    <div className="championship-tab-content">
      <div className="form-section champ-status-section">
        <div className="champ-status-row">
          <span className={`champ-status-badge ${isDraft ? 'draft' : 'live'}`}>
            {isDraft ? 'Draft' : 'Live'}
          </span>
          <span className="champ-status-hint">
            {isDraft
              ? 'Hidden from public website and Discord. Toggle to Live to publish.'
              : 'Visible on public website. Discord messages are active.'}
          </span>
          <button
            type="button"
            className={`champ-status-toggle ${isDraft ? 'publish' : 'unpublish'}`}
            onClick={() => onChange({ status: isDraft ? 'live' : 'draft' })}
          >
            {isDraft ? 'Publish (set Live)' : 'Unpublish (set Draft)'}
          </button>
        </div>
      </div>

      <div className="form-section">
        <h3>SimGrid Integration</h3>
        <div className="form-row" style={{ alignItems: 'flex-end', flexWrap: 'wrap', gap: '8px' }}>
          <div className="form-group" style={{ flex: '1 1 200px' }}>
            <label>Import from SimGrid</label>
            <input
              type="number"
              placeholder="SimGrid championship ID"
              value={simgridImportId}
              onChange={(e) => setSimgridImportId(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ flex: '0 0 auto', paddingTop: '22px' }}>
            <button
              type="button"
              className="add-event-button"
              onClick={handleSimgridImport}
              disabled={simgridImporting || !simgridImportId.trim()}
            >
              {simgridImporting ? 'Importing…' : 'Fetch from SimGrid'}
            </button>
          </div>
        </div>
        {championship.simgridChampionshipId != null && (
          <>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)', margin: '4px 0 8px' }}>
              Synced with SimGrid #{championship.simgridChampionshipId}
            </p>
            <div className="form-row">
              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="simgridManaged"
                    checked={championship.simgridManaged || false}
                    onChange={(e) => handleChange('simgridManaged', e.target.checked)}
                  />
                  <label htmlFor="simgridManaged">Managed via SimGrid</label>
                </div>
                <small style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                  When enabled, entrylist and sign-outs are synced from SimGrid every 5 minutes and before server launch.
                  Disable to manage registrations and sign-outs internally after the initial import.
                </small>
              </div>
            </div>
          </>
        )}
        {simgridImportError && (
          <p style={{ fontSize: '0.85rem', color: 'var(--error-color, #f87171)', margin: '4px 0 0' }}>
            {simgridImportError}
          </p>
        )}
      </div>

      <div className="form-section">
        <h3>
          <Info size={20} />
          Basic Information
        </h3>
        <div className="form-row">
          <div className="form-group">
            <label>Championship Name *</label>
            <input
              type="text"
              value={championship.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., MX5 Cup"
            />
          </div>
          <div className="form-group">
            <label>Community Abbreviation *</label>
            <input
              type="text"
              value={championship.communityAbbreviation}
              onChange={(e) => handleChange('communityAbbreviation', e.target.value)}
              placeholder="e.g., SSRI"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Season Number</label>
            <input
              type="number"
              min="1"
              value={championship.season || 1}
              onChange={(e) => handleChange('season', parseInt(e.target.value) || 1)}
            />
          </div>
          <div className="form-group">
            <label>Recurrence</label>
            <select
              value={championship.recurrence}
              onChange={(e) => handleChange('recurrence', e.target.value)}
            >
              {recurrenceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Grid Spots</label>
            <input
              type="number"
              min="0"
              value={championship.spots || 0}
              onChange={(e) => handleChange('spots', parseInt(e.target.value) || 0)}
              placeholder="Maximum number of entrants"
            />
            {minTrackSlots !== null && (championship.spots || 0) > minTrackSlots && (
              <small style={{ color: 'var(--warning-color, #f59e0b)', fontSize: '12px' }}>
                Warning: one or more tracks only have {minTrackSlots} pit spots available.
              </small>
            )}
            {minTrackSlots !== null && (championship.spots === 0 || !championship.spots) && (
              <small style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                Suggested: {minTrackSlots} (fewest pit spots across selected tracks)
              </small>
            )}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>
          <Settings size={20} />
          Championship Settings
        </h3>
        <div className="form-row">
          <div className="form-group">
            <label>Drop Rounds</label>
            <input
              type="number"
              min="0"
              value={championship.dropRounds || 0}
              onChange={(e) => handleChange('dropRounds', parseInt(e.target.value) || 0)}
              placeholder="Number of worst rounds to drop"
            />
          </div>
          <div className="form-group">
            <label>Same Cars for All Races</label>
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="sameCarsForAllRaces"
                checked={championship.sameCarsForAllRaces || false}
                onChange={(e) => handleChange('sameCarsForAllRaces', e.target.checked)}
              />
              <label htmlFor="sameCarsForAllRaces">
                Use the same car selection for all events
              </label>
            </div>
          </div>
        </div>

        {championship.sameCarsForAllRaces && (
          <div className="form-row shared-cars-selection-row">
            <div className="form-group shared-cars-selection-group">
              <label>Allowed Cars for All Events</label>
              <CarSelectionTable
                cars={cars}
                selectedCars={sharedAllowedCars.map((car) => car.car_name)}
                carConfigs={new Map(
                  sharedAllowedCars.map((car) => [
                    car.car_name,
                    { ballast: car.ballast, restrictor: car.restrictor },
                  ])
                )}
                onSelectionChange={handleSharedCarSelectionChange}
                onConfigChange={handleSharedCarConfigChange}
              />
              <small style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                Select the cars available for all events when "Same Cars for All Races" is enabled.
              </small>
            </div>
          </div>
        )}

        <div className="form-row">
          <div className="form-group points-system-group">
            <label>Points System</label>
            <button type="button" className="points-system-button" onClick={() => setShowPointsModal(true)}>
              Edit Points System
            </button>
            <small style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
              {pointsSystem.slice(0, 5).join(', ')}{pointsSystem.length > 5 ? ', …' : ''}
            </small>
          </div>
        </div>

        <div className="form-row championship-pit-stop-rules">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={championship.mandatoryPitStop || false}
                disabled={!championship.events?.length || championship.events.some((event) =>
                  event.rounds?.some((round) => (round.raceLength || 0) < 1200)
                )}
                onChange={(e) => handleChange('mandatoryPitStop', e.target.checked)}
              />
              Mandatory Pit Stop
            </label>
            {(!championship.events?.length || championship.events.some((event) =>
              event.rounds?.some((round) => (round.raceLength || 0) < 1200)
            )) && <small>All races must be at least 20 minutes.</small>}
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={championship.requiresTyreChange || false}
                disabled={!championship.mandatoryPitStop}
                onChange={(e) => handleChange('requiresTyreChange', e.target.checked)}
              />
              Mandatory Tyres
            </label>
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={championship.requiresRefuelling || false}
                disabled={!championship.mandatoryPitStop}
                onChange={(e) => handleChange('requiresRefuelling', e.target.checked)}
              />
              Mandatory Fuel
            </label>
          </div>
          <div className="form-group">
            <label>Pit Window (sec)</label>
            <input
              type="number"
              value={championship.pitWindow || 0}
              disabled={!championship.mandatoryPitStop}
              onChange={(e) => handleChange('pitWindow', Math.max(0, Math.min(parseInt(e.target.value || '0', 10), shortestRaceDuration)))}
              min="0"
              max={shortestRaceDuration || undefined}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>
          <Users size={20} />
          Entry List & Registrations
        </h3>
        <div className="form-row">
          <div className="form-group">
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="enableEntryList"
                checked={championship.enableEntryList || false}
                onChange={(e) => handleChange('enableEntryList', e.target.checked)}
              />
              <label htmlFor="enableEntryList">
                Enable Entry List Management
              </label>
            </div>
            <small style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
              When enabled, you can manage the entry list in the Entry List tab
            </small>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="enableRegistrations"
                checked={championship.enableRegistrations || false}
                onChange={(e) => handleChange('enableRegistrations', e.target.checked)}
              />
              <label htmlFor="enableRegistrations">
                Enable Public Registrations
              </label>
            </div>
            <small style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
              Allow users to register for this championship on the public website
            </small>
          </div>
        </div>

        {championship.enableRegistrations && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <Calendar size={14} />
                  Registration Deadline
                </label>
                <input
                  type="datetime-local"
                  value={championship.registrationDeadline || ''}
                  onChange={(e) => handleChange('registrationDeadline', e.target.value)}
                />
                <small style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                  Registrations will close at this date and time
                </small>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="allowLateRegistrations"
                    checked={championship.allowLateRegistrations || false}
                    onChange={(e) => handleChange('allowLateRegistrations', e.target.checked)}
                  />
                  <label htmlFor="allowLateRegistrations">
                    Allow registrations during ongoing championship
                  </label>
                </div>
                <small style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                  When enabled, drivers can register after the first event has started. Changing registration details (car, number) is still blocked once the championship begins.
                </small>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="form-section">
        <h3>Server Access</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Driver Password</label>
            <input
              type="text"
              value={championship.driverPassword || ''}
              onChange={(e) => handleChange('driverPassword', e.target.value)}
              placeholder="Leave blank for no password"
            />
            <small style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
              Applied to all servers created for this championship. Shown to registered drivers on the public site.
            </small>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>
          <Radio size={20} />
          Broadcasting
        </h3>
        <div className="form-row">
          <div className="form-group">
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="broadcasted"
                checked={championship.broadcasted || false}
                onChange={(e) => handleChange('broadcasted', e.target.checked)}
              />
              <label htmlFor="broadcasted">
                This championship is broadcasted live
              </label>
            </div>
            <small style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
              When enabled, a link to your configured broadcast channel will appear on the live server card and championship pages.
            </small>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Description & Rules</h3>
        <div className="form-group">
          <label>Description</label>
          <textarea
            value={championship.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Championship description for the public website..."
            rows={4}
          />
        </div>

        <div className="form-group">
          <label>Rules</label>
          <div className="wc-rulebook-type-toggle">
            <button
              type="button"
              className={`wc-type-btn${(championship.rulebookType || 'text') === 'text' ? ' active' : ''}`}
              onClick={() => handleChange('rulebookType', 'text')}
            >
              Raw text
            </button>
            <button
              type="button"
              className={`wc-type-btn${championship.rulebookType === 'link' ? ' active' : ''}`}
              onClick={() => handleChange('rulebookType', 'link')}
            >
              Link
            </button>
            <button
              type="button"
              className={`wc-type-btn${championship.rulebookType === 'pdf' ? ' active' : ''}`}
              onClick={() => handleChange('rulebookType', 'pdf')}
            >
              PDF upload
            </button>
          </div>

          {rulebookType === 'text' && (
            <textarea
              className="wc-rulebook"
              placeholder="Enter championship rules here (Markdown supported) or leave blank to inherit default rulebook."
              value={championship.rulebook || ''}
              onChange={(e) => handleChange('rulebook', e.target.value)}
              rows={6}
            />
          )}

          {rulebookType === 'link' && (
            <label className="wc-field">
              <span>Rulebook URL</span>
              <input
                type="text"
                className="wc-text-input"
                placeholder="https://docs.google.com/..."
                value={championship.rulebook || ''}
                onChange={(e) => handleChange('rulebook', e.target.value)}
              />
            </label>
          )}

          {rulebookType === 'pdf' && (
            <div className="wc-pdf-upload">
              <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    const dataUrl = typeof reader.result === 'string' ? reader.result : null;
                    handleChange('rulebookPdfDataUrl', dataUrl);
                  };
                  reader.readAsDataURL(file);
                }}
              />
              <button
                type="button"
                className="wc-type-btn active"
                onClick={() => pdfInputRef.current?.click()}
              >
                {championship.rulebookPdfDataUrl ? 'Replace PDF' : 'Choose PDF'}
              </button>
              {championship.rulebookPdfDataUrl && (
                <span className="wc-pdf-ready">PDF attached for this championship override.</span>
              )}
            </div>
          )}
        </div>
      </div>
      {showPointsModal && (
        <div className="points-modal-overlay">
          <div className="points-modal">
            <div className="points-modal-header">
              <h3>Points System</h3>
              <button className="modal-close-button" type="button" onClick={() => setShowPointsModal(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="points-schema-buttons">
              <button type="button" onClick={() => applyPointsSchema([50, 36, 30, 24, 20, 16, 12, 8, 4, 2, 1, 1, 1, 0, 0])}>
                Le Mans
              </button>
              <button type="button" onClick={() => applyPointsSchema([25, 18, 15, 12, 10, 8, 6, 4, 2, 1, 0, 0, 0, 0, 0])}>
                Sprint
              </button>
            </div>
            <div className="points-input-list">
              {Array.from({ length: Math.max(pointsSystem.length, 15) }, (_, index) => (
                <div key={index} className="points-input-row">
                  <label>Position {index + 1}</label>
                  <input
                    type="number"
                    min={0}
                    value={pointsSystem[index] || 0}
                    onChange={(e) => setPointsAtPosition(index, Number(e.target.value))}
                  />
                </div>
              ))}
            </div>
            <div className="points-modal-actions">
              <button type="button" onClick={() => onChange({ pointsSystem: Array.from({ length: 15 }, () => 0) })}>
                Reset
              </button>
              <button type="button" onClick={() => setShowPointsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChampionshipDetailsTab;
